import { defineStore } from 'pinia'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'

export const useSettingsStore = defineStore('settings', {
  state: () => ({
    dateFormat: 'US',
    locale: 'en',
    relativeTime: true,
    compactLists: false,
    emailOnIncident: true,
    emailOnResolve: true,
    weeklyDigest: false,
    notificationsSaving: false,
    notificationsSaveError: null,
    sessionTimeout: '2h',
    confirmDangerousActions: true,
  }),
  actions: {
    initialize(user = null) {
      const storedFormat = localStorage.getItem('settings.dateFormat')
      const storedLocale = localStorage.getItem('settings.locale')
      const nextFormat = storedFormat === 'DE' ? 'DE' : 'US'
      const nextLocale = storedLocale === 'de' || storedLocale === 'en'
        ? storedLocale
        : (nextFormat === 'DE' ? 'de' : 'en')
      this.dateFormat = nextFormat
      this.locale = nextLocale
      this.relativeTime = this.readBoolean('settings.relativeTime', true)
      this.compactLists = this.readBoolean('settings.compactLists', false)
      this.applyNotificationSettings(user?.notification_settings ?? null)
      this.confirmDangerousActions = this.readBoolean('settings.confirmDangerousActions', true)
      this.sessionTimeout = this.readSessionTimeout(localStorage.getItem('settings.sessionTimeout'))
    },
    setDateFormat(format) {
      this.dateFormat = format
      localStorage.setItem('settings.dateFormat', format)
    },
    setLocale(locale) {
      const nextLocale = locale === 'de' ? 'de' : 'en'
      this.locale = nextLocale
      localStorage.setItem('settings.locale', nextLocale)
    },
    setRelativeTime(value) {
      this.relativeTime = Boolean(value)
      localStorage.setItem('settings.relativeTime', this.relativeTime ? '1' : '0')
    },
    setCompactLists(value) {
      this.compactLists = Boolean(value)
      localStorage.setItem('settings.compactLists', this.compactLists ? '1' : '0')
    },
    async setEmailOnIncident(value) {
      await this.updateNotificationSettings({ emailOnIncident: Boolean(value) })
    },
    async setEmailOnResolve(value) {
      await this.updateNotificationSettings({ emailOnResolve: Boolean(value) })
    },
    async setWeeklyDigest(value) {
      await this.updateNotificationSettings({ weeklyDigest: Boolean(value) })
    },
    setSessionTimeout(value) {
      this.sessionTimeout = this.readSessionTimeout(value)
      localStorage.setItem('settings.sessionTimeout', this.sessionTimeout)
    },
    setConfirmDangerousActions(value) {
      this.confirmDangerousActions = Boolean(value)
      localStorage.setItem('settings.confirmDangerousActions', this.confirmDangerousActions ? '1' : '0')
    },
    readBoolean(key, defaultValue) {
      const storedValue = localStorage.getItem(key)
      if (storedValue === null) {
        return defaultValue
      }

      return storedValue === '1'
    },
    readSessionTimeout(value) {
      const allowedValues = ['30m', '2h', '8h']

      return allowedValues.includes(value) ? value : '2h'
    },
    applyNotificationSettings(settings) {
      const resolvedSettings = settings && typeof settings === 'object' ? settings : {}

      this.emailOnIncident = this.readNotificationSetting(resolvedSettings, 'email_on_incident', true)
      this.emailOnResolve = this.readNotificationSetting(resolvedSettings, 'email_on_resolve', true)
      this.weeklyDigest = this.readNotificationSetting(resolvedSettings, 'weekly_digest', false)
    },
    readNotificationSetting(settings, key, defaultValue) {
      if (Object.prototype.hasOwnProperty.call(settings, key)) {
        return Boolean(settings[key])
      }

      return defaultValue
    },
    async updateNotificationSettings(changes = {}) {
      const previousValues = {
        emailOnIncident: this.emailOnIncident,
        emailOnResolve: this.emailOnResolve,
        weeklyDigest: this.weeklyDigest,
      }

      if (Object.prototype.hasOwnProperty.call(changes, 'emailOnIncident')) {
        this.emailOnIncident = Boolean(changes.emailOnIncident)
      }

      if (Object.prototype.hasOwnProperty.call(changes, 'emailOnResolve')) {
        this.emailOnResolve = Boolean(changes.emailOnResolve)
      }

      if (Object.prototype.hasOwnProperty.call(changes, 'weeklyDigest')) {
        this.weeklyDigest = Boolean(changes.weeklyDigest)
      }

      this.notificationsSaving = true
      this.notificationsSaveError = null

      try {
        const response = await api.patch('/api/v1/me/notification-settings', {
          email_on_incident: this.emailOnIncident,
          email_on_resolve: this.emailOnResolve,
          weekly_digest: this.weeklyDigest,
        })

        this.applyNotificationSettings(response?.data?.data?.notification_settings ?? null)

        const authStore = useAuthStore()
        authStore.setUser(response?.data?.data ?? authStore.user)
      } catch (error) {
        this.emailOnIncident = previousValues.emailOnIncident
        this.emailOnResolve = previousValues.emailOnResolve
        this.weeklyDigest = previousValues.weeklyDigest
        this.notificationsSaveError = error?.response?.data?.message ?? 'Unable to save notification settings.'
      } finally {
        this.notificationsSaving = false
      }
    },
  },
})
